import { Auction } from './auction';
import { Bid } from './bid';
import { Dictionary } from 'src/app/shared/helpers/util';
import { ItemType } from './auction-item';

class ItemBidState {

    private totalBid: number;

    constructor(public bids: Bid[]) {
        this.parseBids();
    }

    get totalBids() { return this.bids.length; }
    get bidTotal() { return this.totalBid; }

    private parseBids() {
        if (this.bids.length > 0) {
            this.totalBid = this.bids.map(m => m.value).reduce((a, b) => a + b);
        }
    }
}

export class AuctionState {

    private bidTotal: string;
    private wonTotal: string;
    private paidTotal: string;
    private bidOnItems: Dictionary<ItemBidState> = {}; // key = item, number = count of bids
    private maxBids: Dictionary<number> = {}; // key = item, number = max bid
    private winningItems: number[] = [];
    private watched: number[] = [];
    private unpaidAmount: string;

    constructor(public userId: number, public auction: Auction) {
        this.refresh();
    }

    get currentTotal() { return this.bidTotal; }
    get currentWinCount() { return this.winningItems.length; }
    get watchedItems() { return this.watched; }
    get totalOwed() { return this.unpaidAmount; }
    get totalPaid() { return this.paidTotal; }
    get totalWon() { return this.wonTotal; }
    get hasBidOnItems() { return Object.keys(this.bidOnItems).length > 0; }


    hasBidOnItem(itemId: number) {
        return (this.bidOnItems[itemId] != null);
    }

    getBidCount(itemId: number) {
        if (this.bidOnItems[itemId] == null) {
            return 0;
        }
        return this.bidOnItems[itemId].totalBids;
    }

    getBidTotal(itemId: number) {
        if (this.bidOnItems[itemId] == null) {
            return 0;
        }
        return this.bidOnItems[itemId].bidTotal;
    }

    getMaxBid(itemId: number) {
        if (this.maxBids[itemId] == null) {
            return 0;
        }
        return this.maxBids[itemId];
    }

    isWinningItem(itemId: number) {
        return (this.winningItems.indexOf(itemId) > -1);
    }

    isWatchingItem(itemId: number) {
        return (this.watchedItems.indexOf(itemId) > -1);
    }

    refresh() {
        this.getBidState();
        this.getFollowedItems();
    }

    update() {
        this.getBidState();
    }

    toggleItemFollowing(itemId: number) {
        const idx = this.watched.indexOf(itemId);
        if (idx > -1) {
            this.watched.splice(idx, 1);
        } else {
            this.watched.push(itemId);
        }
    }

    private getBidState() {
        // clear old data
        let bidTotal = 0;
        let wonTotal = 0;
        let owedTotal = 0;
        this.bidOnItems = {};
        this.winningItems = [];

        for (const id of Object.keys(this.auction.products)) {
            const item = this.auction.products[id];
            // First figure out if we have bid on the item or not since this doesn't really matter the type
            const bids = item.bids.filter(m => m.userId === this.userId);
            if (bids.length > 0) {
                // Store that we bid and the count
                this.bidOnItems[+id] = new ItemBidState(bids);
                // Okay, based on item type we figure out if the user has bid/won it yet

                if (item.type === ItemType.BlindBid) {
                    // For blind items we either include or exclude based on auction state
                    if ((item.winningUserId === this.userId && item.closeVerified) || item.hasClosed === false) {
                        // We always need to filter the bids so that we get our own max
                        const max = bids.reduce((prev, current) => (prev.amount > current.amount) ? prev : current);
                        // If the auction has ended and we met the reserve then we won
                        if (item.hasClosed === true) {
                            this.winningItems.push(+id);
                            if (item.reserveMet === true) {
                                wonTotal += max.value;
                                if (item.paidFor === false) {
                                    owedTotal +=  max.value;
                                }
                            }
                        } else {
                            bidTotal += max.value;
                        }
                    }
                } else if (item.isBidOn && item.winningUserId === this.userId) {
                    this.winningItems.push(+id);
                    // If the item is closed and not paid for.. then we need to add it to the unpaid amount
                    if (item.hasClosed === true && item.reserveMet === true) {
                        wonTotal += item.currentBid;
                        if (item.paidFor === false) {
                            owedTotal += item.currentBid;
                        }
                    } else if (item.hasClosed === false) {
                        bidTotal += item.currentBid;
                    }
                } else if (item.isBidOn === false) {
                    // Any items not bid on use a cumulative total
                    const bidValues = item.bids.filter(m => m.userId === this.userId).map(m => m.value);
                    let total = 0;
                    if (bidValues.length > 0) {
                        total = bidValues.reduce((a, b) => a + b);
                        if (total > 0) {
                            this.winningItems.push(+id);
                        }
                    }
                    wonTotal += total;

                    // any bids that have not been paid for need to be added to the unpaid amount for donate/buy
                    owedTotal += item.bids.filter(m => m.userId === this.userId && m.paidFor === false)
                        .reduce((a, b) => a + b.value, 0);
                }
            }
            // Check if we have any max bids for these items. If so then if the amount > our max.. then null the max?
            if (this.maxBids[id] != null && this.maxBids[id] < item.minimumBid) {
                this.maxBids[id] = null;
            }
        }

        // Set the current values
        this.bidTotal = `${this.auction.currencySymbol}${bidTotal}`;
        this.wonTotal = `${this.auction.currencySymbol}${wonTotal}`;

        if (owedTotal > 0) {
            this.unpaidAmount = `${this.auction.currencySymbol}${owedTotal}`;
        } else {
            this.unpaidAmount = null;
        }

        // determine how much was paid
        const paid = wonTotal - owedTotal;
        this.paidTotal = `${this.auction.currencySymbol}${paid}`;
    }

    private getFollowedItems() {
        this.watched = [];
        // Determine what products we are watching
        if (this.auction.watchedItems != null && this.auction.watchedItems.length > 0) {
            const watching = this.auction.watchedItems.split(',');
            watching.forEach(m => {
                this.watchedItems.push(+m);
            })
        }
    }
}

